import { Button, Stack } from "@mui/material";
import type { FC, ReactNode } from "react";
import { useState } from "react";
import type { ProductResponse } from "../admin/products/productsApi";
import type { UserContext } from "../auth/auth";
import ConfirmationDialog from "../components/confirmationDialog/ConfirmationDialog";
import ParticipantCountDiscrepancyDialog from "../components/participantCountDiscrepancyDialog/ParticipantCountDiscrepancyDialog";
import { findSaveDiscrepancies, type SaveDiscrepancies } from "../sharedComponents/participantCountValidation";
import CalculationCancelButton from "./CalculationCancelButton";
import type { CalculationType } from "./calculationTypes";
import DownloadCalculationPdfButton from "./downloadPdfButton/DownloadCalculationPdfButton";
import DownloadPriceListButton from "./downloadPriceListButton/DownloadPriceListButton";
import CalculationEmailButton from "./emails/CalculationEmailButton";

const confirmSaveText =
  "Czy chcesz zapisać nową wersję kalkulacji? Po zapisaniu zaktualizowana kalkulacja zostanie wysłana emailem.";

export interface SaveValidationInput {
  readonly participantsNumber: { readonly numberOfPaid: number; readonly numberOfStaff: number };
  readonly products: {
    readonly id: string;
    readonly options: { readonly id: string; readonly quantity: number }[];
  }[];
}

interface Props {
  readonly editMode: boolean;
  readonly context: UserContext | null;
  readonly readonly: boolean;
  readonly isCustomCalculation: boolean;
  readonly calculation:
    | {
        readonly id: string;
        readonly customerId: string;
        readonly version: number;
        readonly type: CalculationType;
        readonly hasDynamicPriceProducts: boolean;
        readonly hasProductsForQuote: boolean;
        readonly hasPlaneTickets: boolean;
      }
    | undefined;
  readonly calculationName: string;
  readonly onDelete: () => void | Promise<unknown>;
  readonly onEdit: () => void | Promise<unknown>;
  readonly onSave: () => void | Promise<unknown>;
  /** When in edit mode, pass to run participant-count validation before saving. */
  readonly saveValidationInput?: SaveValidationInput;
  readonly productDetails: ProductResponse[];
  readonly onUpdatePrices: () => void | Promise<unknown>;
  readonly onOrder: (orderFromProviders: boolean) => void | Promise<unknown>;
  readonly onRequestQuote: () => Promise<void>;
  readonly onCheckAvailability: () => void;
}

const EditCalculationActions: FC<Props> = ({
  editMode,
  context,
  readonly,
  isCustomCalculation,
  calculation,
  calculationName,
  onDelete,
  onEdit,
  onSave,
  saveValidationInput,
  productDetails,
  onUpdatePrices,
  onOrder,
  onRequestQuote,
  onCheckAvailability,
}) => {
  const [confirmationDialog, setConfirmationDialog] = useState<{
    title: string;
    content: ReactNode;
    onConfirm: () => Promise<void>;
  }>();
  const [discrepancyModal, setDiscrepancyModal] = useState<SaveDiscrepancies | null>(null);

  const openVersionConfirmDialog = () => {
    setConfirmationDialog({
      title: "Zapisz nową wersję",
      content: confirmSaveText,
      onConfirm: async () => {
        await onSave();
        setConfirmationDialog(undefined);
      },
    });
  };

  const handleOrder = (orderFromProviders: boolean) => {
    setConfirmationDialog({
      title: "Złóż zamówienie",
      content: (
        <>
          Czy chcesz złożyć zamówienie na podstawie kalkulacji? Kalkulacji nie będzie już można modyfikować.
          {calculation?.hasPlaneTickets && (
            <>
              <br />
              <br />
              Po potwierdzeniu rezerwacji zawierającej bilety lotnicze anulacja jest możliwa wyłącznie odpłatnie - 350
              zł do 65 dni przed wylotem.
            </>
          )}
        </>
      ),
      onConfirm: async () => {
        await onOrder(orderFromProviders);
        setConfirmationDialog(undefined);
      },
    });
  };

  const handleSave = () => {
    if (saveValidationInput && productDetails.length > 0) {
      const totalParticipants =
        saveValidationInput.participantsNumber.numberOfPaid + saveValidationInput.participantsNumber.numberOfStaff;
      const discrepancies = findSaveDiscrepancies(totalParticipants, saveValidationInput.products, productDetails);
      if (discrepancies) {
        setDiscrepancyModal(discrepancies);
        return;
      }
    }
    openVersionConfirmDialog();
  };

  const handleDiscrepancyConfirm = async () => {
    await onSave();
    setDiscrepancyModal(null);
  };

  const handleDelete = () => {
    setConfirmationDialog({
      title: "Usuń ofertę",
      content: "Czy chcesz usunąć ofertę? Tej operacji nie będzie można już cofnąć.",
      onConfirm: async () => {
        await onDelete();
        setConfirmationDialog(undefined);
      },
    });
  };

  const handleRequestQuote = () => {
    setConfirmationDialog({
      title: "Zamów wycenę",
      content: "Czy chcesz wysłać prośbę do dostawcy o przygotowanie wyceny dla opcji 'na zapytanie'?",
      onConfirm: async () => {
        await onRequestQuote();
        setConfirmationDialog(undefined);
      },
    });
  };

  const isCustomer = context?.role === "CUSTOMER";
  const canOrderFromProvider =
    context?.role === "ADMIN" ||
    context?.role === "AGENT" ||
    (context?.role === "CUSTOMER" && context?.customPrivileges.canOrderCalculation);
  const isPackageCalculation = calculation?.type === "PACKAGE";
  const editDisabled =
    isCustomer &&
    (isCustomCalculation || isPackageCalculation) &&
    (!context?.customPrivileges.canCreateCalculationFromProducts || isPackageCalculation);
  return (
    <>
      <Stack direction="row" spacing={2}>
        <CalculationCancelButton />
        {!readonly && (
          <Button variant="outlined" color="error" onClick={handleDelete}>
            Usuń
          </Button>
        )}
        {!editMode && !readonly && (
          <Button variant="outlined" color="secondary" onClick={onEdit} disabled={editDisabled}>
            Edytuj
          </Button>
        )}
        {!editMode && (
          <DownloadPriceListButton
            calculationId={calculation!.id}
            version={calculation!.version}
            calculationName={calculationName}
          />
        )}
        {!editMode && (
          <DownloadCalculationPdfButton
            calculationId={calculation!.id}
            calculationType={calculation!.type}
            version={calculation!.version}
            filename={`${calculationName}.pdf`}
          />
        )}
        {!isCustomer && !editMode && (
          <CalculationEmailButton
            calculationId={calculation!.id}
            customerId={calculation!.customerId}
            calculationName={calculationName}
          />
        )}
        {editMode && !readonly && (
          <Button variant="outlined" color="primary" onClick={handleSave}>
            Zapisz
          </Button>
        )}
        {!editMode && !readonly && calculation?.hasDynamicPriceProducts && (
          <Button variant="outlined" color="secondary" onClick={onUpdatePrices}>
            Aktualizuj ceny
          </Button>
        )}
        {!readonly && calculation?.hasDynamicPriceProducts && (
          <Button variant="outlined" color="secondary" onClick={onCheckAvailability}>
            Sprawdź dostępność
          </Button>
        )}
        {!editMode && !readonly && calculation?.hasProductsForQuote && (
          <Button variant="outlined" onClick={handleRequestQuote}>
            Zamów wycenę
          </Button>
        )}
        {!editMode && !readonly && (
          <Button variant="outlined" color="primary" onClick={() => handleOrder(false)}>
            Zamów
          </Button>
        )}
        {!editMode && !readonly && canOrderFromProvider && (
          <Button variant="outlined" color="primary" onClick={() => handleOrder(true)}>
            Zamów u dostawcy
          </Button>
        )}
      </Stack>
      <ConfirmationDialog
        open={!!confirmationDialog}
        title={confirmationDialog?.title ?? ""}
        content={confirmationDialog?.content}
        onCancel={() => setConfirmationDialog(undefined)}
        onConfirm={confirmationDialog?.onConfirm}
      />
      <ParticipantCountDiscrepancyDialog
        discrepancies={discrepancyModal}
        additionalInfo={confirmSaveText}
        onConfirm={handleDiscrepancyConfirm}
        onCancel={() => setDiscrepancyModal(null)}
      />
    </>
  );
};

export default EditCalculationActions;
